/**
 * [INPUT]: AdminApi 的 S3 配置读取、保存与连接测试端口。
 * [OUTPUT]: 对外提供对象存储系统设置页面，覆盖字段校验、凭据替换、连接测试与脱敏运行状态。
 * [POS]: admin-web/pages 的基础设施配置边界；与 LLM 渠道页面共享管理员会话和反馈模式，不感知 HTTP 细节。
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { CheckCircle2, CircleAlert, CloudCog, Eye, EyeOff, FlaskConical, RefreshCcw, Save, ShieldCheck } from 'lucide-react';
import { adminApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import { useAsync } from '../hooks/use-async';
import { Badge, Button, Card, CardHeader, ErrorState, Input, LoadingBlock, Select, Spinner } from '../components/ui';
import type { S3Provider, S3SettingsInput } from '../types';

const emptyForm: S3SettingsInput = {
  enabled: true,
  provider: 's3_compatible',
  endpoint: 'http://localhost:9000',
  region: 'us-east-1',
  bucket: 'lifeverse',
  publicBaseUrl: 'http://192.168.1.21:9000/lifeverse',
  accessKeyId: '',
  secretAccessKey: '',
  forcePathStyle: true,
};

type FieldName = 'endpoint' | 'region' | 'bucket' | 'publicBaseUrl' | 'accessKeyId' | 'secretAccessKey';
type FieldErrors = Partial<Record<FieldName, string>>;

export function SystemSettingsPage() {
  const { data, loading, error, reload } = useAsync(() => adminApi.getS3Settings(), []);
  const [form, setForm] = useState<S3SettingsInput>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [activeAction, setActiveAction] = useState<'save' | 'test' | null>(null);
  const [secretVisible, setSecretVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [failure, setFailure] = useState('');
  useEffect(() => {
    if (!data) return;
    setForm({
      enabled: true,
      provider: data.provider || 's3_compatible',
      endpoint: data.endpoint,
      region: data.region || 'us-east-1',
      bucket: data.bucket,
      publicBaseUrl: data.publicBaseUrl,
      accessKeyId: data.accessKeyId,
      secretAccessKey: '',
      forcePathStyle: data.forcePathStyle,
    });
  }, [data]);

  function update<K extends keyof S3SettingsInput>(key: K, value: S3SettingsInput[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setFailure('');
    setMessage('');
    if (key in fieldErrors) setFieldErrors((current) => ({ ...current, [key]: undefined }));
  }

  function changeProvider(provider: S3Provider) {
    setForm((current) => ({
      ...current,
      provider,
      region: current.region || 'us-east-1',
      forcePathStyle: provider !== 'aws_s3',
    }));
    setFailure('');
    setMessage('');
    setFieldErrors((current) => ({ ...current, endpoint: undefined }));
  }

  function validateField(name: FieldName): string | undefined {
    const value = form[name].trim();
    if (name === 'endpoint' && form.provider !== 'aws_s3' && !value) return '当前 Provider 需要填写 Endpoint。';
    if (name === 'endpoint' && value && !isHTTPURL(value)) return 'Endpoint 需要是完整的 HTTP(S) 地址。';
    if (name === 'region' && !value) return '请输入 Region，例如 us-east-1。';
    if (name === 'bucket' && (!value || /[\\/\s]/.test(value))) return 'Bucket 不能为空，也不能包含空格或斜杠。';
    if (name === 'publicBaseUrl' && value && !isHTTPURL(value)) return 'Public Base URL 需要是完整的 HTTP(S) 地址。';
    if (name === 'accessKeyId' && !value) return '请输入 Access Key ID。';
    if (name === 'secretAccessKey' && !value && !data?.secretConfigured) return '首次保存需要填写 Secret Access Key。';
    return undefined;
  }

  function validateForm() {
    const fields: FieldName[] = ['endpoint', 'region', 'bucket', 'publicBaseUrl', 'accessKeyId', 'secretAccessKey'];
    const next: FieldErrors = {};
    for (const field of fields) {
      const validation = validateField(field);
      if (validation) next[field] = validation;
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  function validateOnBlur(field: FieldName) {
    setFieldErrors((current) => ({ ...current, [field]: validateField(field) }));
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!validateForm()) return;
    setActiveAction('save');
    setFailure('');
    setMessage('');
    try {
      await adminApi.saveS3Settings(form);
      setMessage('对象存储配置已保存并热切换。');
      await reload();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : '保存对象存储配置失败。');
    } finally {
      setActiveAction(null);
    }
  }

  async function testConnection() {
    if (!validateForm()) return;
    setActiveAction('test');
    setFailure('');
    setMessage('');
    try {
      const result = await adminApi.testS3Connection(form);
      setMessage(`连接测试成功，延迟 ${result.latencyMs} ms。`);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : '连接测试失败。');
    } finally {
      setActiveAction(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-moss">System / Storage</p>
          <h1 className="mt-2 text-2xl font-semibold tracking-[-0.035em] text-ink sm:text-3xl">对象存储</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">管理封面、图片和音频使用的 S3-compatible 存储。Secret 永不回显。</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void reload()} disabled={loading}>
          <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />刷新
        </Button>
      </div>

      {message && <Feedback tone="success" icon={<CheckCircle2 className="h-4 w-4" />}>{message}</Feedback>}
      {failure && <Feedback tone="danger" icon={<CircleAlert className="h-4 w-4" />}>{failure}</Feedback>}
      {loading && !data ? (
        <Card><div className="space-y-4 p-6"><LoadingBlock className="h-5 w-40" /><LoadingBlock className="h-10 w-full" /><LoadingBlock className="h-10 w-full" /></div></Card>
      ) : error && !data ? (
        <Card><ErrorState message={error.message} onRetry={reload} /></Card>
      ) : (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
          <Card>
            <CardHeader title="主存储配置" description="浏览器只提交新 Secret；留空表示保留数据库中的现有凭据。" />
            <form className="space-y-5 p-5" onSubmit={save} noValidate>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField label="Provider" required>
                  <Select value={form.provider} onChange={(event) => changeProvider(event.target.value as S3Provider)}>
                    <option value="s3_compatible">S3-compatible</option>
                    <option value="aws_s3">AWS S3</option>
                  </Select>
                </FormField>
                <FormField label="Region" required error={fieldErrors.region}>
                  <Input value={form.region} onChange={(event) => update('region', event.target.value)} onBlur={() => validateOnBlur('region')} aria-invalid={Boolean(fieldErrors.region)} placeholder="us-east-1" />
                </FormField>
                <FormField label="Endpoint" required={form.provider !== 'aws_s3'} error={fieldErrors.endpoint} className="sm:col-span-2" hint={form.provider === 'aws_s3' ? 'AWS S3 可留空，使用官方 Endpoint。' : undefined}>
                  <Input value={form.endpoint} onChange={(event) => update('endpoint', event.target.value)} onBlur={() => validateOnBlur('endpoint')} aria-invalid={Boolean(fieldErrors.endpoint)} placeholder={form.provider === 'aws_s3' ? '可选：留空使用 AWS 默认地址' : 'http://localhost:9000'} />
                </FormField>
                <FormField label="Bucket" required error={fieldErrors.bucket}>
                  <Input value={form.bucket} onChange={(event) => update('bucket', event.target.value)} onBlur={() => validateOnBlur('bucket')} aria-invalid={Boolean(fieldErrors.bucket)} placeholder="elseyou-assets" />
                </FormField>
                <FormField label="Public Base URL" optional error={fieldErrors.publicBaseUrl}>
                  <Input value={form.publicBaseUrl} onChange={(event) => update('publicBaseUrl', event.target.value)} onBlur={() => validateOnBlur('publicBaseUrl')} aria-invalid={Boolean(fieldErrors.publicBaseUrl)} placeholder="https://assets.example.com" />
                </FormField>
                <FormField label="Access Key ID" required error={fieldErrors.accessKeyId}>
                  <Input value={form.accessKeyId} onChange={(event) => update('accessKeyId', event.target.value)} onBlur={() => validateOnBlur('accessKeyId')} aria-invalid={Boolean(fieldErrors.accessKeyId)} autoComplete="username" />
                </FormField>
                <FormField label="Secret Access Key" required={!data?.secretConfigured} error={fieldErrors.secretAccessKey} hint={data?.secretConfigured ? '已保存密文；留空保持不变。' : undefined}>
                  <div className="relative">
                    <Input type={secretVisible ? 'text' : 'password'} autoComplete="new-password" value={form.secretAccessKey} onChange={(event) => update('secretAccessKey', event.target.value)} onBlur={() => validateOnBlur('secretAccessKey')} aria-invalid={Boolean(fieldErrors.secretAccessKey)} className="pr-12" placeholder={data?.secretConfigured ? '已配置，留空保持不变' : '填写 Secret'} />
                    <button type="button" className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-moss" onClick={() => setSecretVisible((current) => !current)} aria-label={secretVisible ? '隐藏 Secret Access Key' : '显示 Secret Access Key'}>
                      {secretVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </FormField>
              </div>

              <div className="flex flex-wrap gap-x-5 gap-y-3 rounded-xl border border-line bg-slate-50 p-4 text-sm text-slate-700">
                <Toggle checked={form.forcePathStyle} onChange={(value) => update('forcePathStyle', value)}>Path-style 请求</Toggle>
              </div>

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => void testConnection()} disabled={activeAction !== null}>
                  {activeAction === 'test' ? <Spinner /> : <FlaskConical className="h-3.5 w-3.5" />}{activeAction === 'test' ? '测试中…' : '测试连接'}
                </Button>
                <Button type="submit" className="w-full sm:w-auto" disabled={activeAction !== null}>
                  {activeAction === 'save' ? <Spinner /> : <Save className="h-3.5 w-3.5" />}{activeAction === 'save' ? '保存中…' : '保存配置'}
                </Button>
              </div>
            </form>
          </Card>

          <Card>
            <CardHeader title="运行状态" description="服务端运行时视角" />
            <div className="space-y-4 p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-mint text-moss"><CloudCog className="h-5 w-5" /></div>
                <div><p className="text-sm font-semibold text-ink">{data?.configured ? '配置已就绪' : '尚未配置'}</p><p className="mt-1 text-xs text-slate-500">来源：{sourceLabel(data?.source)}</p></div>
              </div>
              <Badge status={data?.enabled && data.configured ? 'active' : 'paused'}>{data?.enabled && data.configured ? '运行时已启用' : data?.enabled ? '已启用但未就绪' : '运行时未启用'}</Badge>
              <div className="border-t border-line pt-4 text-xs leading-5 text-slate-500">
                <p className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-moss" />Secret 仅在服务端密文存储，管理员 API 和审计记录均不会返回它。</p>
                <p className="mt-3">最近更新：{formatDate(data?.updatedAt ?? null, true)}</p>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function FormField({ label, required = false, optional = false, error, hint, className, children }: { label: string; required?: boolean; optional?: boolean; error?: string; hint?: string; className?: string; children: ReactNode }) {
  return <label className={className}><span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">{label}{required && <span className="ml-1 text-danger">*</span>}{optional && <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">（可选）</span>}</span>{children}{error ? <span className="mt-1.5 block text-[11px] leading-4 text-danger">{error}</span> : hint ? <span className="mt-1.5 block text-[11px] leading-4 text-slate-400">{hint}</span> : null}</label>;
}

function Toggle({ checked, onChange, children }: { checked: boolean; onChange: (checked: boolean) => void; children: ReactNode }) {
  return <label className="flex min-h-8 cursor-pointer items-center gap-2"><input type="checkbox" className="h-4 w-4 accent-moss" checked={checked} onChange={(event) => onChange(event.target.checked)} />{children}</label>;
}

function Feedback({ tone, icon, children }: { tone: 'success' | 'danger'; icon: ReactNode; children: ReactNode }) {
  return <div className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-xs ${tone === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-800'}`}>{icon}{children}</div>;
}

function isHTTPURL(value: string) {
  try {
    const parsed = new URL(value);
    return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && Boolean(parsed.host) && !parsed.username && !parsed.password;
  } catch {
    return false;
  }
}

function sourceLabel(source: 'database' | 'environment' | 'none' | undefined) {
  if (source === 'database') return '数据库';
  if (source === 'environment') return '环境变量';
  return '无';
}
